import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkGeofence } from '@/domain';
import { useAuthGateway, useClock, useLocationProvider } from '../container';
import { keys } from '../query-keys';
import type { Credentials, Session } from '../ports/AuthGateway';

/**
 * The session is server state, so it lives in TanStack Query rather than in the
 * Zustand store. Section 2 is explicit that server data does not go in Zustand,
 * and a session held in two places is a session that disagrees with itself the
 * first time an administrator changes the access matrix.
 */
export function useSession() {
  const auth = useAuthGateway();
  const clock = useClock();

  return useQuery({
    queryKey: keys.session,
    queryFn: async (): Promise<Session | null> => {
      const session = await auth.session();
      // Shared floor devices drift. Correct from the server on every read.
      if (session !== null) clock.syncTo(session.serverTime);
      return session;
    },
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Thrown when the credentials were right but the device is not at the lab.
 *
 * A distinct type so the login screen can say WHY rather than showing the
 * password error, which would send a technician round in circles retyping a
 * password that was never the problem.
 */
export class OutsideLabError extends Error {
  constructor(
    readonly refusal: 'outside' | 'unavailable',
    readonly metresAway: number | null,
  ) {
    // The wording lives in the strings module, which this layer may not
    // import. The screen turns the reason into copy.
    super(refusal);
    this.name = 'OutsideLabError';
  }
}

export function useSignIn() {
  const auth = useAuthGateway();
  const clock = useClock();
  const location = useLocationProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: Credentials) => {
      const session = await auth.signIn(credentials);

      /*
       * The fence is checked AFTER authenticating, because knowing whether it
       * applies needs the role and the role comes with the session. A refusal
       * signs straight back out, so nothing but this function ever sees a
       * session that should not exist.
       *
       * The location is only requested when the fence is on and applies to
       * this role. Asking every user for their location on every sign in, to
       * then ignore it, would train people to deny the permission prompt.
       */
      const fence = session.geofence;
      if (!fence.enabled || !fence.appliedRoles.includes(session.role)) return session;

      const position = await location.current();
      const verdict = checkGeofence(fence, session.role, position);

      /*
       * The server writes down where the sign in came from, inside or out,
       * before a refusal signs back out. Only a log: a failure to send it
       * neither stops a sign in nor explains a refusal. Section 28 of the
       * review of 2 October 2026.
       */
      await auth.reportSignInLocation(position).catch(() => undefined);
      if (verdict.allowed) return session;

      await auth.signOut();
      throw new OutsideLabError(
        verdict.reason,
        verdict.reason === 'outside' ? verdict.metresAway : null,
      );
    },
    onSuccess: (session) => {
      clock.syncTo(session.serverTime);
      /*
       * Whatever is cached belonged to whoever used this device before, and the
       * devices on the floor are shared. Signing out empties the cache, but a
       * session that ran out without a sign out did not, and the next person
       * read the last person's bell and lists until the first refetch. So
       * everything but the session goes here too. The session itself is then
       * seeded rather than invalidated: the sign in response already carries
       * it, and refetching it would be a second round trip on the slowest
       * connection in the building. Request of 24 September 2026.
       */
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== keys.session[0],
      });
      queryClient.setQueryData(keys.session, session);
    },
  });
}

/**
 * Changing your own password. Section 4 of the request of 13 September 2026.
 *
 * No invalidation: nothing cached changes when a password does. The session
 * making the call stays signed in; the server ends every other one.
 */
export function useChangePassword() {
  const auth = useAuthGateway();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      auth.changePassword(input.currentPassword, input.newPassword),
    // The session says whether a change is still owed; reread it.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.session }),
  });
}

export function useSignOut() {
  const auth = useAuthGateway();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => auth.signOut(),
    onSettled: () => {
      // Everything cached belonged to the person who just left. Devices on the
      // floor are shared, so this is a privacy boundary and not housekeeping.
      queryClient.setQueryData(keys.session, null);
      queryClient.clear();
    },
  });
}
